import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Message } from '../models/common/message.model';
import { PaginationOptions } from '../models/common/pagination-options.model';
import { CurrentPlan } from '../models/corporate/current-plan.model';
import { DownloadInvoicePdf } from '../models/corporate/download-invoice-pdf.model';
import { PaginatedSubscriptionHistory } from '../models/corporate/paginated-subscriptions-history.model';
import { BillingInfo, PaymentMethodTokenInfo, PaymentMethodTokenInitial } from '../models/corporate/payment-method-token.model';
import { Plans } from '../models/corporate/plans.model';
import { SubscriptionRecurring } from '../models/corporate/subscriptions-recurring.model';
import { SubscriptionHistory } from '../models/corporate/suscription-history.model';
import { StripeSetupIntentResponse, StripePaymentMethodStatus, StripeSubscriptionResponse } from '../models/corporate/stripe-payment.model';

@Injectable({ providedIn: 'root' })
export class CorporateService {
  private readonly baseUrl = environment.apiUrl;
  private readonly plansUrl = `${this.baseUrl}corporate/`;

  readonly currentPlans = signal<Plans | null>(null);

  readonly currentPaymentMethod = signal<PaymentMethodTokenInfo | null>(null);

  readonly currentSubscription = signal<CurrentPlan | null>(null);

  readonly paymentHistory = signal<SubscriptionHistory[] | null>(null);
  readonly totalHistory = signal<number | null>(null);

  readonly currentPage = signal<number | null>(null);
  readonly pageSize = signal<number | null>(null);
  readonly totalPages = signal<number | null>(null);

  constructor(private http: HttpClient) { }

  getPlan(): Observable<Plans> {
    return this.http.get<Plans>(`${this.plansUrl}subscription/plans`, { withCredentials: true });
  }

  private getPaymentMethodToken(): Observable<PaymentMethodTokenInfo> {
    // Stripe, no Payzen: esa pasarela fue dada de baja y su endpoint
    // devolvía 'sin método de pago' aunque la empresa sí tuviera tarjeta.
    return this.http.get<PaymentMethodTokenInfo>(`${this.plansUrl}payment-method/info`, { withCredentials: true });
  }

  /**
   * @deprecated Payzen dio de baja la cuenta comercial: esto responde 500.
   * Solo queda porque la compra de planes en COP todavía cuelga de aquí
   * (ver plan.component). El alta de tarjeta ya NO lo usa: pasó a
   * createStripeSetupIntent().
   */
  createPaymentMethodToken(): Observable<PaymentMethodTokenInitial> {
    return this.http.post<PaymentMethodTokenInitial>(`${this.plansUrl}payment-method/token`, {}, { withCredentials: true });
  }

  createNewRecurringSubscription(planId: string): Observable<SubscriptionRecurring> {
    return this.http.post<SubscriptionRecurring>(`${this.plansUrl}subscriptions/recurring`, { planId }, { withCredentials: true });
  }

  deleteRecurringSubscription(): Observable<Message> {
    return this.http.delete<Message>(`${this.plansUrl}subscriptions/recurring`, { withCredentials: true });
  }

  // ─── Stripe (planes en USD) — separado del flujo Payzen/COP de arriba ───

  createStripeSetupIntent(): Observable<StripeSetupIntentResponse> {
    return this.http.post<StripeSetupIntentResponse>(`${this.plansUrl}payment-method/setup-intent`, {}, { withCredentials: true });
  }

  getStripePaymentMethodStatus(): Observable<StripePaymentMethodStatus> {
    return this.http.get<StripePaymentMethodStatus>(`${this.plansUrl}payment-method`, { withCredentials: true });
  }

  createStripeSubscription(planId: string, billingInterval: 'monthly' | 'annual' = 'monthly'): Observable<StripeSubscriptionResponse> {
    return this.http.post<StripeSubscriptionResponse>(`${this.plansUrl}subscriptions/stripe`, { planId, billingInterval }, { withCredentials: true });
  }

  updateInfoBilling(info: BillingInfo): Observable<BillingInfo> {
    return this.http.put<BillingInfo>(`${this.plansUrl}payment-method/billing-info`, info, { withCredentials: true });
  }

  downloadInvoicePdf(invoiceId: string): Observable<DownloadInvoicePdf> {
    return this.http.get<DownloadInvoicePdf>(`${this.plansUrl}invoices/${invoiceId}/download`, {withCredentials: true });
  }

  private getCurrentPlans(): Observable<CurrentPlan> {
    return this.http.get<CurrentPlan>(`${this.plansUrl}subscription`, { withCredentials: true });
  }

  private getPaymentHistory(options: PaginationOptions): Observable<PaginatedSubscriptionHistory> {
    let params = new HttpParams()
      .set('page', options.page)
      .set('size', options.size);

    if (options.search) params = params.set('search', options.search);
    if (options.sortBy) params = params.set('sortBy', options.sortBy);
    if (options.sortDir) params = params.set('sortDir', options.sortDir);

    if (options.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        params = params.set(key, value);
      });
    }
    return this.http.get<PaginatedSubscriptionHistory>(`${this.plansUrl}subscription/payment-history`, { params, withCredentials: true });
  }


  loadPlans() {
    return this.getPlan().pipe(
      tap((data: Plans) => {
        this.currentPlans.set(data);
      }),
      catchError((error) => {
        console.error('Error loading plans:', error);
        return of(null);
      })
    );
  }

  loadPaymentMethodToken() {
    return this.getPaymentMethodToken().pipe(
      tap((data: PaymentMethodTokenInfo) => {
        this.currentPaymentMethod.set(data);
      }),
      catchError((error) => {
        console.error('Error loading payment method token:', error);
        return of(null);
      })
    );
  }

  startTrial(): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}corporate/trial/start`, {}, { withCredentials: true }).pipe(
      tap(() => this.loadCurrentSubscription().subscribe()),
    );
  }

  loadCurrentSubscription() {
    return this.getCurrentPlans().pipe(
      tap((data: CurrentPlan) => {
        this.currentSubscription.set(data);
      }),
      catchError((error) => {
        console.error('Error loading current subscription:', error);
        return of(null);
      })
    );
  }

  loadPaymentHistoryPaginated(options: PaginationOptions) {
    return this.getPaymentHistory(options).pipe(
      tap((data: PaginatedSubscriptionHistory) => {
        this.paymentHistory.set(data.subscriptionHistory);
        this.totalHistory.set(data.totalElements);
        this.currentPage.set(data.currentPage);
        this.pageSize.set(data.pageSize);
        this.totalPages.set(data.totalPages);
      }),
      catchError((error) => {
        console.error('Error loading payment history:', error);
        this.paymentHistory.set([]);
        return of({
          subscriptionHistory: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: options.page,
          pageSize: options.size
        });
      })
    );
  }

}